/**
 * Consolidated date-formatting utilities. Was split across
 * `utils/{dateFormatter,dateFormatterToISO}.js` and `utils/DateFormatters/
 * formatDateTime.js` - three genuinely different output shapes, not
 * duplicates, so kept as distinct exports rather than merged into one
 * function.
 */

// The organisation's time zone, once it has chosen one (Organisation details). Every formatter below
// shows dates in it; until then (null) they show the browser's local time, as before. Display only:
// stored dates and billing schedules stay UTC.
let displayTimeZone = null;

export const setDisplayTimeZone = (timeZone) => {
  displayTimeZone = timeZone || null;
};
export const getDisplayTimeZone = () => displayTimeZone;

/** Calendar parts of `date` in the display zone (or the browser's when none is set). */
const zonedParts = (date) => {
  const parts = {};
  new Intl.DateTimeFormat("en-GB", {
    timeZone: displayTimeZone || undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23",
  })
    .formatToParts(date)
    .forEach((p) => {
      parts[p.type] = p.value;
    });
  const hours24 = Number(parts.hour) % 24;
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    minutes: parts.minute,
    hours12: hours24 % 12 || 12,
    ampm: hours24 >= 12 ? "PM" : "AM",
  };
};

/** `DD-MM-YYYY` - was `dateFormatter.js`'s `dateFormatter`. */
export const formatShortDate = (dateString) => {
  const dateObject = new Date(dateString);
  if (isNaN(dateObject.getTime())) return ""; // invalid date

  const { day, month, year } = zonedParts(dateObject);

  return `${day}-${month}-${year}`;
};

/** `DD/MM/YYYY, h:mm AM/PM` - was `dateFormatter.js`'s `dateTimeFormatter12Hour`. */
export const formatDateThenTime = (dateString) => {
  const dateObject = new Date(dateString);
  if (isNaN(dateObject.getTime())) return ""; // invalid date

  const { day, month, year, hours12: hours, minutes: mins, ampm } = zonedParts(dateObject);

  return `${day}/${month}/${year}, ${hours}:${mins} ${ampm}`;
};

/**
 * `h:mm AM/PM, DD/MM/YYYY` - was `utils/DateFormatters/formatDateTime.js`.
 * The most-used date formatter in the app (28+ consumers, mostly table
 * column renderers) - kept its exact name, only its location changed.
 */
export const formatDateTime = (isoString) => {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return ""; // invalid date

  const { day, month, year, hours12: hours, minutes, ampm } = zonedParts(date);

  return `${hours}:${minutes} ${ampm}, ${day}/${month}/${year}`;
};

/**
 * `MMM D, YYYY` e.g. "Aug 1, 2026" - was invoices' local `formatMonthDate`.
 * Returns `undefined` (not `""`) for a missing/invalid date so consumers
 * like `TextCell`'s `children ?? "—"` fallback still kick in.
 */
export const formatMonthDate = (dateString) => {
  if (!dateString) return undefined;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: displayTimeZone || undefined,
  });
};

/**
 * `MMM D, YYYY, h:mm AM/PM` e.g. "Aug 1, 2026, 5:30 AM" - formatMonthDate
 * plus a time-of-day, for columns (like Usage Collection's ingestion
 * timestamp) where the time genuinely matters and formatMonthDate's
 * date-only shape would drop information the old formatDateTime column
 * used to show. Same `undefined`-on-invalid contract as formatMonthDate.
 */
export const formatMonthDateTime = (dateString) => {
  if (!dateString) return undefined;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return undefined;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: displayTimeZone || undefined,
  });
};

/** `Xm/h/d/mo/y ago` - always relative, never falls back to an absolute date. */
export const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const seconds = Math.round((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;

  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;

  const years = Math.round(months / 12);
  return `${years}y ago`;
};

/**
 * Converts a date string to a UTC ISO string, e.g. "2025-03-02T18:30:00Z".
 * Was `dateFormatterToISO.js`, which used `moment` - converted to native
 * `Date` per `docs/package-registry.md`'s standing rule against propagating
 * `moment` usage into newly-migrated code. Output is byte-for-byte
 * identical to the old moment-based result (milliseconds stripped to match).
 */
export const formatDateToISO = (dateString) => {
  const date = new Date(dateString);
  return date.toISOString().replace(/\.\d{3}Z$/, "Z");
};
